import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SkillAssessmentCardProps {
  title: string;
  label: string;
  score: number;
  maxScore: number;
  why: string;
  suggestion: string;
  strengths?: string[];
  toImprove?: string[];
  note?: string;
}

export const SkillAssessmentCard: React.FC<SkillAssessmentCardProps> = ({
  title,
  label,
  score,
  maxScore,
  why,
  suggestion,
  strengths,
  toImprove,
  note
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const percentage = (score / maxScore) * 100;
  
  const getScoreColor = (percent: number) => {
    if (percent >= 80) return 'text-green-600';
    if (percent >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 80) return 'bg-green-500';
    if (percent >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{title || label}</h3>
            {note && <p className="text-sm text-gray-500 mt-1">{note}</p>}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={cn("text-2xl font-bold", getScoreColor(percentage))}>
                {score}
              </div>
              <div className="text-sm text-gray-500">/ {maxScore}</div>
            </div>
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${(2 * Math.PI * 28 * percentage) / 100} ${2 * Math.PI * 28}`}
                  className={getProgressColor(percentage)}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold">{Math.round(percentage)}%</span>
              </div>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
          <div className="pt-4">
            <div className="flex items-start gap-2 mb-3">
              <div className="w-1 h-full bg-blue-500 rounded-full mt-1" />
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Why this score?</h4>
                <p className="text-gray-700">{why}</p>
              </div>
            </div>

            <div className="flex items-start gap-2 mb-3">
              <div className="w-1 h-full bg-purple-500 rounded-full mt-1" />
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Suggestion</h4>
                <p className="text-gray-700">{suggestion}</p>
              </div>
            </div>

            {strengths && strengths.length > 0 && (
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Strengths
                </h4>
                <ul className="space-y-2">
                  {strengths.map((strength, index) => (
                    <li key={index} className="text-gray-700 pl-6 relative">
                      <span className="absolute left-0 top-1 w-2 h-2 bg-green-500 rounded-full" />
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {toImprove && toImprove.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  To Improve
                </h4>
                <ul className="space-y-2">
                  {toImprove.map((improvement, index) => (
                    <li key={index} className="text-gray-700 pl-6 relative">
                      <span className="absolute left-0 top-1 w-2 h-2 bg-orange-500 rounded-full" />
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};